/**
 * Operator-facing compared inputs for gate checkpoint cards.
 * Drawn from verification evidence (+ optional request snapshot), not inventing values.
 */

export type ComparedValueRow = { label: string; value: string };

function asNonEmptyString(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
}

function asStringList(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const parts = v
    .map((x) => (typeof x === 'string' ? x.trim() : String(x ?? '').trim()))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function numKm(v: unknown): string | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return `${Math.round(v)} km`;
}

function push(rows: ComparedValueRow[], label: string, value: string | null | undefined) {
  if (value) rows.push({ label, value });
}

function gateEvidenceSlice(
  evidence: Record<string, unknown> | null | undefined,
  gate: string,
): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const slice = (nested as Record<string, unknown>)[gate];
    if (slice && typeof slice === 'object' && !Array.isArray(slice)) {
      return slice as Record<string, unknown>;
    }
  }
  const top = evidence[gate];
  if (top && typeof top === 'object' && !Array.isArray(top)) {
    return top as Record<string, unknown>;
  }
  return {};
}

function legitimacyEvidence(evidence: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const leg = (nested as Record<string, unknown>).LEGITIMACY;
    if (leg && typeof leg === 'object' && !Array.isArray(leg)) {
      return { ...evidence, ...(leg as Record<string, unknown>) };
    }
  }
  const gateEv = evidence.LEGITIMACY;
  if (gateEv && typeof gateEv === 'object' && !Array.isArray(gateEv)) {
    return { ...evidence, ...(gateEv as Record<string, unknown>) };
  }
  return evidence;
}

function formatPct(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value <= 1) return `${Math.round(value * 100)}%`;
  return `${Math.round(value)}%`;
}

function humanizeToken(value: string): string {
  return value
    .split(/[_/\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function linkageEvidence(evidence: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const linkage = (nested as Record<string, unknown>).LINKAGE;
    if (linkage && typeof linkage === 'object' && !Array.isArray(linkage)) {
      return { ...evidence, ...(linkage as Record<string, unknown>) };
    }
  }
  const gateEv = evidence.LINKAGE;
  if (gateEv && typeof gateEv === 'object' && !Array.isArray(gateEv)) {
    return { ...evidence, ...(gateEv as Record<string, unknown>) };
  }
  return evidence;
}

function structuralEvidence(evidence: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const structural = (nested as Record<string, unknown>).STRUCTURAL_SANITY;
    if (structural && typeof structural === 'object' && !Array.isArray(structural)) {
      return { ...evidence, ...(structural as Record<string, unknown>) };
    }
  }
  const gateEv = evidence.STRUCTURAL_SANITY;
  if (gateEv && typeof gateEv === 'object' && !Array.isArray(gateEv)) {
    return { ...evidence, ...(gateEv as Record<string, unknown>) };
  }
  return evidence;
}

function snapshot(evidence: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const snap = evidence?.requestSnapshot;
  return snap && typeof snap === 'object' && !Array.isArray(snap)
    ? (snap as Record<string, unknown>)
    : {};
}

/**
 * Returns label/value pairs for the given checkpoint step, or [] when unknown.
 */
export function comparedValuesForCheckpointStep(
  step: string,
  evidence: Record<string, unknown> | null | undefined,
): ComparedValueRow[] {
  const ev = structuralEvidence(evidence);
  const snap = snapshot(evidence);
  const jobTitle =
    asNonEmptyString(ev.role) ??
    asNonEmptyString(snap.jobTitle) ??
    asNonEmptyString(ev.jobTitle);
  const sector =
    asNonEmptyString(ev.applicationSectorLabel) ??
    asNonEmptyString(ev.resolvedDeclaredIndustry) ??
    asNonEmptyString(snap.applicationSectorLabel) ??
    asNonEmptyString(snap.resolvedDeclaredIndustry) ??
    asNonEmptyString(ev.sector) ??
    asNonEmptyString(snap.sector);
  const workingType =
    asNonEmptyString(ev.workingType) ?? asNonEmptyString(snap.workingType);
  const rows: ComparedValueRow[] = [];
  const stepU = step.trim().toUpperCase();

  switch (stepU) {
    case 'SECTOR_ROLE_MATCH':
      push(rows, 'Job title', jobTitle);
      push(rows, 'Declared sector', sector);
      push(rows, 'Resolved NOC', asNonEmptyString(ev.resolvedNocCode));
      push(rows, 'NOC title', asNonEmptyString(ev.resolvedNocTitle));
      break;
    case 'ONSITE_ROLE_VS_REMOTE':
      push(rows, 'Job title', jobTitle);
      push(rows, 'Working type', workingType);
      push(rows, 'Remote policy', asNonEmptyString(ev.remotePolicySource));
      break;
    case 'DECLARED_SECTOR_VS_EMPLOYER_NAICS':
      push(rows, 'Declared sector', sector);
      push(rows, 'Employer NAICS', asNonEmptyString(ev.employerNaicsCode));
      push(rows, 'Expected NAICS', asStringList(ev.declaredSectorExpectedNaics));
      push(rows, 'Alignment', asNonEmptyString(ev.sectorNaicsAlignment));
      break;
    case 'INFRA_MATCH':
    case 'SECTOR_INFRA_MISMATCH':
      push(rows, 'Job title', jobTitle);
      push(rows, 'Declared sector', sector);
      push(rows, 'Expected workplace', asStringList(ev.expectedInfrastructure));
      push(rows, 'Observed workplace', asStringList(ev.inferredInfraCategories));
      push(rows, 'Primary business type', asNonEmptyString(ev.primaryBusinessType));
      break;
    case 'COMMUTE_DISTANCE':
      push(rows, 'Commute distance', numKm(ev.commuteDistanceKm));
      push(rows, 'Working type', workingType);
      push(rows, 'Employer address', asNonEmptyString(snap.employerAddressNormalized));
      break;
    case 'CROSS_PROVINCE_COMMUTE':
      push(rows, 'Working type', workingType);
      push(
        rows,
        'Applicant province',
        asNonEmptyString(ev.applicantProvinceCode) ??
          asNonEmptyString(snap.applicantProvinceCode),
      );
      push(
        rows,
        'Employer province',
        asNonEmptyString(ev.employerProvinceCode) ?? asNonEmptyString(snap.employerProvinceCode),
      );
      push(rows, 'Employer address', asNonEmptyString(snap.employerAddressNormalized));
      break;
    case 'EMPLOYMENT_CONTEXT_CONTRADICTION': {
      const ctxAssessment = ev.employmentContextAssessment as Record<string, unknown> | undefined;
      const signals =
        ctxAssessment?.signals && typeof ctxAssessment.signals === 'object'
          ? (ctxAssessment.signals as Record<string, unknown>)
          : undefined;
      push(rows, 'Declared sector', sector);
      push(rows, 'Job title', jobTitle);
      push(rows, 'Workplace signals', asStringList(ev.placeTypes));
      push(rows, 'Occupation match', asNonEmptyString(signals?.occupationCategoryMatch));
      push(rows, 'Sector match', asNonEmptyString(signals?.sectorMatch));
      break;
    }
    case 'DIGITAL_FOOTPRINT': {
      const leg = legitimacyEvidence(evidence);
      const rating = leg.placeRating;
      if (typeof rating === 'number' && Number.isFinite(rating)) {
        push(rows, 'Google rating', `${rating}/5`);
      }
      if (typeof leg.placeRatingCount === 'number') {
        push(rows, 'Review count', String(leg.placeRatingCount));
      }
      push(rows, 'Pass threshold', '≥3★ / ≥5 reviews');
      const companyCount = leg.companyImageCount;
      const satelliteCount = leg.satelliteImageCount;
      if (typeof companyCount === 'number' || typeof satelliteCount === 'number') {
        const parts: string[] = [];
        if (typeof companyCount === 'number') {
          parts.push(`${companyCount} listing photo${companyCount === 1 ? '' : 's'}`);
        }
        if (typeof satelliteCount === 'number') {
          parts.push(`${satelliteCount} satellite image${satelliteCount === 1 ? '' : 's'}`);
        }
        push(rows, 'Site media', parts.join(' · '));
      }
      break;
    }
    case 'OPERATIONAL_STATUS': {
      const leg = legitimacyEvidence(evidence);
      push(rows, 'Google status', asNonEmptyString(leg.placeBusinessStatus));
      push(rows, 'Expected status', 'OPERATIONAL');
      push(rows, 'Data source', 'Google Places');
      break;
    }
    case 'PLACE_PHOTOS_VISION': {
      const leg = legitimacyEvidence(evidence);
      push(rows, 'Category', humanizeToken(asNonEmptyString(leg.photoVisionCategory) ?? '') || null);
      push(rows, 'Confidence', formatPct(leg.photoVisionConfidence));
      push(rows, 'Media type', 'Maps listing photos');
      break;
    }
    case 'SATELLITE_BUILDING_VISION': {
      const leg = legitimacyEvidence(evidence);
      push(rows, 'Category', humanizeToken(asNonEmptyString(leg.satelliteVisionCategory) ?? '') || null);
      push(rows, 'Confidence', formatPct(leg.satelliteVisionConfidence));
      push(rows, 'Media type', 'Satellite imagery');
      break;
    }
    case 'AI_DIGITAL_FOOTPRINT': {
      const leg = legitimacyEvidence(evidence);
      const ai = leg.aiDigitalFootprint as Record<string, unknown> | undefined;
      if (ai && typeof ai === 'object') {
        push(rows, 'AI match', asNonEmptyString(ai.detectedEmployer));
        push(rows, 'Confidence', formatPct(ai.overallConfidence));
        push(rows, 'Pass threshold', formatPct(ai.confidenceThreshold));
        if (typeof ai.isRealCompany === 'boolean') {
          push(rows, 'Real company', ai.isRealCompany ? 'Yes' : 'No');
        }
        push(rows, 'Official website', asNonEmptyString(ai.officialWebsite));
        push(rows, 'LinkedIn', asNonEmptyString(ai.linkedInUrl));
        push(rows, 'Business number', asNonEmptyString(ai.businessNumber));
        push(rows, 'Corporation ID', asNonEmptyString(ai.corporationId));
        push(rows, 'Sector', asNonEmptyString(ai.sector));
        push(rows, 'Origin country', asNonEmptyString(ai.companyOriginCountry));
        push(rows, 'Phone', asNonEmptyString(ai.phone));
        const branch = ai.matchedBranch as Record<string, unknown> | undefined;
        if (branch && typeof branch === 'object') {
          const branchParts = [
            asNonEmptyString(branch.name),
            asNonEmptyString(branch.address),
            asNonEmptyString(branch.phone),
          ].filter(Boolean);
          push(rows, 'Matched branch', branchParts.length ? branchParts.join(' · ') : null);
        }
      }
      break;
    }
    case 'ACTIVE_DIRECTORS': {
      const leg = legitimacyEvidence(evidence);
      const count = leg.activeDirectorCount;
      if (typeof count === 'number') {
        push(rows, 'Active directors', String(count));
      }
      const source = asNonEmptyString(leg.activeDirectorSource);
      if (source) {
        push(
          rows,
          'Source',
          source === 'federal_registry'
            ? 'Federal corporate registry'
            : source === 'ai_digital_footprint'
              ? 'AI digital footprint'
              : source === 'skipped'
                ? 'Not required (public sector)'
                : humanizeToken(source),
        );
      }
      const officers = leg.aiOfficers;
      if (Array.isArray(officers) && officers.length > 0) {
        const listed = officers
          .slice(0, 2)
          .map((o) => {
            if (!o || typeof o !== 'object') return '';
            const row = o as Record<string, unknown>;
            const name = asNonEmptyString(row.name);
            const role = asNonEmptyString(row.role);
            return name ? (role ? `${name} (${role})` : name) : '';
          })
          .filter(Boolean)
          .join('; ');
        push(rows, 'Officers cited', listed || null);
      }
      break;
    }
    case 'PUBLIC_HIRING_ACTIVITY': {
      const leg = legitimacyEvidence(evidence);
      const hiring = leg.publicHiring as Record<string, unknown> | undefined;
      if (hiring && typeof hiring === 'object') {
        push(rows, 'Hiring signal', asNonEmptyString(hiring.outcomeCopy));
        if (typeof hiring.postingCount === 'number') {
          push(rows, 'Postings found', String(hiring.postingCount));
        }
        push(rows, 'Source', asNonEmptyString(hiring.sourceKind));
      }
      break;
    }
    case 'WEB_DOMAIN': {
      const leg = gateEvidenceSlice(evidence, 'LEGITIMACY');
      const raw = (leg.webDomain ?? legitimacyEvidence(evidence).webDomain) as
        | Record<string, unknown>
        | undefined;
      if (raw && typeof raw === 'object') {
        push(rows, 'Website', asNonEmptyString(raw.sourceUrl));
        push(rows, 'Domain', asNonEmptyString(raw.domain));
        push(rows, 'WHOIS status', asNonEmptyString(raw.status));
        push(rows, 'Registrar', asNonEmptyString(raw.registrar));
        push(rows, 'Registered', asNonEmptyString(raw.registeredOn));
        push(rows, 'Lookup', asNonEmptyString(raw.lookup));
      }
      break;
    }
    case 'AI_LINKAGE_ANALYSIS': {
      const link = linkageEvidence(evidence);
      const ai = link.aiLinkageAnalysis as Record<string, unknown> | undefined;
      if (ai && typeof ai === 'object') {
        const looksLike = asNonEmptyString(ai.looksLike);
        const matchAssessment = asNonEmptyString(ai.matchAssessment);
        const suggested = asNonEmptyString(ai.suggestedOutcome);
        push(rows, 'Payor type (AI)', looksLike ? humanizeToken(looksLike) : null);
        push(rows, 'Name alignment (AI)', matchAssessment ? humanizeToken(matchAssessment) : null);
        push(rows, 'Advisory confidence', formatPct(ai.confidence));
        push(rows, 'Suggested route', suggested ? humanizeToken(suggested) : null);
      }
      push(rows, 'Bank payor (memo)', asNonEmptyString(link.bankTransactionString ?? evidence?.bankTransactionString));
      break;
    }
    case 'DIRECT_PAYOR_MATCH':
    case 'PAYROLL_PROVIDER_MATCH':
    case 'UNKNOWN_PAYOR': {
      const link = linkageEvidence(evidence);
      push(rows, 'Bank payor (memo)', asNonEmptyString(link.bankTransactionString ?? evidence?.bankTransactionString));
      push(
        rows,
        'Match result',
        typeof link.directMatchPercent === 'number'
          ? `${Math.round(link.directMatchPercent)}% overlap`
          : null,
      );
      break;
    }
    default:
      break;
  }

  return rows;
}
